import { useQuery } from "@tanstack/react-query";
import { Claim_ClaimsDbModel } from "@safelease/service-utilities";
import http from "../services/http-common";

interface UseClaimsParams {
  relationshipId?: number;
  locationId?: number | null;
}

interface UseClaimsReturn {
  claims: Claim_ClaimsDbModel[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch claims from the REST API using the standard axios instance
 * Auth headers and error handling are managed by http-common interceptors
 *
 * @param relationshipId - Required to filter claims by relationship
 * @param locationId - Optional to filter by location. null = "All Locations", undefined = not provided
 */
async function fetchClaims(relationshipId?: number, locationId?: number | null): Promise<Claim_ClaimsDbModel[]> {
  // Build query parameters
  const params: Record<string, string> = {};
  if (relationshipId !== undefined) {
    params.relationshipId = String(relationshipId);
  }
  if (locationId !== undefined && locationId !== null) {
    params.locationId = String(locationId);
  }

  // Make request using standardized axios instance
  // Auth headers are automatically added by http-common interceptors
  const response = await http.get("/claims", { params });

  return response.data.claims || [];
}

/**
 * Custom hook to fetch claims using TanStack Query
 * Provides automatic caching, refetching, and error handling
 */
export function useClaims({ relationshipId, locationId }: UseClaimsParams): UseClaimsReturn {
  const {
    data: claims = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Unique query key based on parameters
    queryKey: ["claims", { relationshipId, locationId }],
    // Fetch function
    queryFn: () => fetchClaims(relationshipId, locationId),
    // Only fetch if we have at least one parameter defined
    // relationshipId is typically always provided, locationId can be null (All Locations) or undefined (not provided)
    enabled: relationshipId !== undefined || locationId !== undefined,
    // Keep data fresh for 5 minutes to reduce unnecessary API calls
    staleTime: 5 * 60 * 1000,
    // Keep unused data in cache for 10 minutes
    cacheTime: 10 * 60 * 1000,
  });

  return {
    claims,
    loading: isLoading,
    error: error as Error | null,
    refetch,
  };
}
